import PageMeta from '@/components/PageMeta';
import SectionHeader from '@/components/SectionHeader';
import Pipeline from '@/components/Pipeline';

const LAYERS = [
  {
    n: '01',
    t: 'Phone-camera movement capture',
    b: 'Interpreting visible body movement from ordinary smartphone video — no wearables, no specialised camera systems, no calibration rig.',
  },
  {
    n: '02',
    t: 'Movement consistency',
    b: 'Reducing misleading variation caused by perspective, tracking instability, or incomplete visual information where possible.',
  },
  {
    n: '03',
    t: 'Relative effort and movement behaviour',
    b: 'Exploring visible changes in speed, control, repetition, and movement behaviour — without claiming to directly measure hidden physical forces a camera cannot see.',
  },
  {
    n: '04',
    t: 'Changing movement states',
    b: 'Identifying when movement behaviour appears to change during a repetition or session — slowing, instability, asymmetry.',
  },
  {
    n: '05',
    t: 'Reliability awareness',
    b: 'Recognising when conflicting signals, poor visibility, camera conditions, or unusual movement make a confident conclusion less appropriate.',
  },
];

export default function Technology() {
  return (
    <div>
      <PageMeta
        title="Technology"
        description="Movement intelligence designed for imperfect real life. Five plain-language layers of how Valanti Labs works."
      />
      <section className="vl-container py-16 lg:py-24">
        <p className="mono-label text-vl-violet">Technology</p>
        <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mt-5 text-vl-ink max-w-3xl">
          Movement intelligence designed for imperfect real life.
        </h1>
        <p className="mt-6 text-lg text-vl-ink/70 max-w-2xl leading-relaxed">
          Valanti Labs is not simply another pose-estimation product. The research has explored a deeper question: how
          can ordinary smartphone video be translated into movement information that is more mechanically meaningful,
          stable, and honest about uncertainty?
        </p>
      </section>

      <section className="bg-vl-bg2 border-y hairline">
        <div className="vl-container py-20 lg:py-28">
          <SectionHeader kicker="The pipeline" title="Five stages, from phone video to honest feedback." />
          <div className="mt-14">
            <Pipeline />
          </div>
        </div>
      </section>

      <section className="vl-container py-24 lg:py-32">
        <SectionHeader kicker="Five plain-language layers" title="What each layer is doing." />
        <div className="mt-14 grid grid-cols-1 lg:grid-cols-2 gap-4">
          {LAYERS.map((l) => (
            <div
              key={l.n}
              className="rounded-3xl bg-vl-surface border hairline p-8 hover:-translate-y-0.5 transition-transform"
            >
              <div className="flex items-baseline gap-4">
                <span className="font-mono text-sm text-vl-violet">{l.n}</span>
                <h3 className="font-serif-display text-2xl text-vl-ink leading-tight">{l.t}</h3>
              </div>
              <p className="text-sm text-vl-ink/70 mt-3 leading-relaxed">{l.b}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-vl-ink text-vl-bg">
        <div className="vl-container py-20 lg:py-28">
          <div className="max-w-3xl">
            <p className="mono-label text-vl-bg/55 mb-4">The differentiator</p>
            <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl leading-[1.05]">
              Better movement technology should not simply produce an answer. It should recognise when the available
              evidence is not reliable enough to justify certainty.
            </h2>
            <p className="mt-6 text-vl-bg/70 leading-relaxed">
              The Valanti Labs research has invested deliberately in reliability awareness. Where the evidence is
              weak — conflicting signals, poor visibility, an unsupported movement, an unusual camera angle — the
              system is designed to say so, rather than produce a confident-looking number you cannot trust.
            </p>
          </div>
        </div>
      </section>

      <section className="vl-container py-20 lg:py-28 max-w-3xl">
        <p className="mono-label text-vl-violet">An honest limitation</p>
        <h2 className="font-serif-display text-3xl text-vl-ink mt-4 leading-tight">
          What Valanti Labs does not claim.
        </h2>
        <p className="mt-5 text-vl-ink/75 leading-relaxed">
          The application does not claim to directly measure absolute physical forces or joint torques that are not
          visible from a single uncalibrated phone camera. It does not diagnose, treat, monitor, or replace medical
          care. It does not guarantee correct movement, prevent injury, or remove the need for professional
          supervision where that is appropriate.
        </p>
      </section>
    </div>
  );
}
